"use client";
import { Button, Drawer, Grid, Modal, Row, Col, Skeleton, Empty } from "antd";
import styles from "./locationSelector.module.css";
import { IconChevronDown, IconSearch, IconMapPin } from "@/lib/icons";
import { useContext, useState, useEffect, useRef } from "react";
import React from "react";
import { locationcontext } from "@/lib/context";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { ENDPOINTS } from "@/constants/endpoint";
import { DEV_AUTH_HEADERS } from "@/lib/devHeaders";
import { sanitizeSearchInput } from "@/lib/validations";

function CityCard({ name, state, country, imgUrl, isSelected, onClick }) {
  const parts = [state, country].filter(Boolean);
  return (
    <button
      onClick={onClick}
      className={`${styles.cityCard} ${isSelected ? styles.cityCardActive : ""}`}
      aria-label={`Select ${name}`}
    >
      {imgUrl ? (
        <Image
          src={imgUrl}
          alt={name}
          fill
          sizes="(max-width: 768px) 45vw, 180px"
          style={{ objectFit: "cover", objectPosition: "center bottom" }}
        />
      ) : (
        // Fallback initial when no image
        <span className={styles.cityCardFallback}>{name[0]}</span>
      )}
      <div className={styles.cityNameWrap}>
        <span className={styles.cityName}>{name}</span>
        {parts.length > 0 && (
          <span className={styles.citySubtext}>{parts.join(", ")}</span>
        )}
      </div>
    </button>
  );
}

/** Plain select-style row — used for live search results (no CMS image) */
function CityOption({ name, state, country, isSelected, onClick }) {
  const parts = [state, country].filter(Boolean);
  return (
    <button
      onClick={onClick}
      className={`${styles.cityOption} ${isSelected ? styles.cityOptionActive : ""}`}
      aria-label={`Select ${name}`}
    >
      <span className={styles.cityOptionName}>{name}</span>
      {parts.length > 0 && (
        <span className={styles.cityOptionSubtext}>{parts.join(", ")}</span>
      )}
    </button>
  );
}

/** Inner modal/drawer body — shared between desktop modal and mobile drawer */
function LocationBody({
  config,
  location,
  updateLocationAndCookie,
  onClose,
  isDrawer,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [apiCities, setApiCities] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const displayCities = config?.display_cities || [];

  // When query is empty show CMS cities; when typing show API results
  const hasCities = searchQuery.trim()
    ? apiCities.length > 0
    : displayCities.length > 0;

  const visibleCities = searchQuery.trim() ? apiCities : displayCities;

  // Debounced API search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setApiCities([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/${ENDPOINTS.CITY_SEARCH}?search=${encodeURIComponent(searchQuery)}&limit=10`,
          {
            method: "GET",
            credentials: "include",
            headers: { ...DEV_AUTH_HEADERS },
          },
        );
        const json = await res.json();
        const list = Array.isArray(json) ? json : json?.data || [];
        setApiCities(
          list.map((c) => ({
            name: c.name || c.city || c.city_name || c.label || "",
            img: c.img || [],
            state: c.state || c.state_name || null,
            country: c.country || c.country_name || null,
          })),
        );
      } catch {
        setApiCities([]);
      } finally {
        setSearching(false);
      }
    }, 350);

    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const handleCitySelect = (cityName, state, country) => {
    onClose();
    updateLocationAndCookie(cityName, true, { state, country });
  };

  return (
    <div className={styles.bodyWrap}>
      {/* Title */}
      {!isDrawer && (
        <div className="modal-title">{config?.title || "Select your city"}</div>
      )}
      {config?.subtitle && (
        <div className="modal-subtitle">{config.subtitle}</div>
      )}

      {/* Search input */}
      <div className={styles.searchBox}>
        <IconSearch size={16} color="var(--color-grey)" />
        <input
          className={styles.searchInput}
          placeholder="Search a city…"
          value={searchQuery}
          onChange={(e) => {
            const val = sanitizeSearchInput(e.target.value);
            setSearchQuery(val);
            if (val.trim()) setSearching(true);
          }}
          autoComplete="off"
          autoFocus
        />
      </div>

      {/* City grid or empty state */}
      {searching ? (
        <div className={styles.cityGrid}>
          <Row
            gutter={[
              { xs: 12, sm: 16, md: 20 },
              { xs: 12, sm: 16, md: 20 },
            ]}
          >
            {Array(6)
              .fill(0)
              .map((_, i) => (
                <Col xs={12} sm={8} md={6} key={i}>
                  <Skeleton.Node
                    active
                    style={{ width: "100%", height: 140, borderRadius: 16 }}
                  />
                </Col>
              ))}
          </Row>
        </div>
      ) : !hasCities ? (
        <div className={styles.emptyWrap}>
          <Empty
            description={
              <span className={styles.emptyText}>
                {searchQuery.trim()
                  ? `No cities found for "${searchQuery}"`
                  : "No cities available yet — check back soon!"}
              </span>
            }
          />
        </div>
      ) : searchQuery.trim() ? (
        // Live search results — plain select-style rows, not image cards
        <div className={styles.cityOptionList}>
          {visibleCities.map((c) => (
            <CityOption
              key={c.name}
              name={c.name}
              state={c.state}
              country={c.country}
              isSelected={
                location?.toLowerCase() ===
                c.name.toLowerCase().replace(/\s+/g, "-")
              }
              onClick={() => handleCitySelect(c.name, c.state, c.country)}
            />
          ))}
        </div>
      ) : (
        <Row
          gutter={[
            { xs: 12, sm: 16, md: 20 },
            { xs: 12, sm: 16, md: 20 },
          ]}
          className={styles.cityGrid}
        >
          {visibleCities.map((c) => (
            <Col xs={12} sm={8} md={6} key={c.name}>
              <CityCard
                name={c.name}
                state={c.state}
                country={c.country}
                imgUrl={c.img?.[0]?.url}
                isSelected={
                  location?.toLowerCase() ===
                  c.name.toLowerCase().replace(/\s+/g, "-")
                }
                onClick={() => handleCitySelect(c.name, c.state, c.country)}
              />
            </Col>
          ))}
        </Row>
      )}

      {!searchQuery.trim() && hasCities && (
        <p className={styles.moreText}>{config?.bottom_text}</p>
      )}
    </div>
  );
}

export default function LocationSelector({
  config: configProp,
  fromBanner = false,
  hideLabel = false,
}) {
  const path = usePathname();
  const { useBreakpoint } = Grid;
  const screen = useBreakpoint();
  const {
    location,
    isOpen,
    setIsOpen,
    loading,
    updateLocationAndCookie,
    setLocation,
    locationSelectorConfig,
  } = useContext(locationcontext);

  // Use the explicitly passed config, fall back to the one in context
  const config = configProp ?? locationSelectorConfig ?? null;

  const searchParams = useSearchParams();
  const is_application = searchParams.get("is_application");
  const queryCity = searchParams.get("city");

  React.useEffect(() => {
    if (is_application === "true" && queryCity) {
      if (isOpen) setIsOpen(false);
      if (location !== queryCity) {
        setLocation(queryCity);
      }
    }
  }, [
    is_application,
    queryCity,
    isOpen,
    location,
    updateLocationAndCookie,
    setIsOpen,
  ]);

  const pathSegments = path.split("/").filter(Boolean);
  const isOldEventDetail =
    path.includes("/events/") && !path.endsWith("/events");
  const isArtistDetail = path.includes("/artist/");
  const isContestDetail = path.includes("/contest/");
  const isNewEventDetail =
    pathSegments[1] === "event" ||
    (pathSegments[2] === "events" && pathSegments.length > 3);
  if (isOldEventDetail || isNewEventDetail || isArtistDetail || isContestDetail)
    return null;

  const isModalOpen = is_application === "true" && queryCity ? false : isOpen;

  const triggerButton = loading ? (
    <Skeleton.Input
      active
      size="large"
      style={{ width: hideLabel ? 110 : 150, borderRadius: 999 }}
    />
  ) : (
    <Button
      type="text"
      className={
        hideLabel
          ? styles.locationSelectorButtonCompact
          : styles.locationSelectorButton
      }
      onClick={() => setIsOpen(true)}
    >
      <span className={styles.locationSelectorWrap}>
        {/* Blue circle icon badge (or plain icon when fromBanner) */}
        <span
          className={`${styles.locationIconBadge} ${
            fromBanner ? styles.locationIconBadgeNoBg : ""
          }`}
        >
          <IconMapPin size={fromBanner ? 18 : 14} stroke={2} />
        </span>
        <span className={styles.locationSelectorText}>
          {!hideLabel && (
            <span className={styles.label}>{config?.intent_text} </span>
          )}
          <span className={`${styles.cityDisplay} capitalize`}>
            {location?.replace(/-/g, " ") || "Select City"}
          </span>
        </span>
        <IconChevronDown size={16} className={styles.chevron} />
      </span>
    </Button>
  );

  const bodyProps = {
    config,
    location,
    updateLocationAndCookie,
    fromBanner,
    onClose: () => setIsOpen(false),
  };

  return (
    <>
      {triggerButton}

      {/* Desktop: Modal */}
      {screen.md ? (
        <Modal
          open={isModalOpen}
          onCancel={() => setIsOpen(false)}
          closable
          maskClosable={!!location}
          footer={null}
          width={900}
          style={{ top: 20 }}
          styles={{ body: { padding: 0 } }}
          destroyOnHidden
        >
          <LocationBody {...bodyProps} />
        </Modal>
      ) : (
        <Drawer
          open={isModalOpen}
          onClose={() => setIsOpen(false)}
          placement="bottom"
          height="88vh"
          closable
          title={config?.title || "Select your city"}
          styles={{
            body: { padding: "0 16px 24px", overflowY: "auto" },
            header: { borderBottom: "1px solid #e3e6e9", padding: "16px" },
          }}
          destroyOnHidden
        >
          <LocationBody {...bodyProps} isDrawer={true} />
        </Drawer>
      )}
    </>
  );
}
